/**
 * @fileoverview
 *
 * Some countries have a preset list of tax rates that need to be mapped to specific tax codes
 * This file contains the logic to validate that the items in the shopping cart only use these properly-configured rates,
 * as well as a checklist/table for the configuration menu
 */
import React from 'react';
import { useSelector } from 'react-redux';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

import { getVatRates } from 'reducers/vatRatesDB';

type ExpectedTaxRate = {
  name: string;
  rate: number;
  group: string;
};
/**
 * A table-like visualization comparing the vat rates on the account to those provided
 *
 * <pre>
 * |               Expected vat rates               |
 * |    EFS expected vat rate    | Erply vat rate   |
 * | class | description  | rate |  rate  |  name   |
 * |-------|--------------|------|--------|---------|
 * |   A   | Normal tax   | 10%  |  10%   | Normal  |
 * |   B   | Car tax      | 12%  |        |         |
 * |   G   | Tax free     |  0%  |        |         |
 * |       |              |      |  12%   | Car tax |
 * |       |              |      |   5%   | Subsidy |
 * </pre>
 *
 * The left columns show the expected rates, the right columns show rates in erply.
 *
 * Where the erply rate is set up correctly (has the class identifier in the 'code' property), it is paired up next to the EFS rate
 * All other rates are on separate rows.
 *
 * If an Erply rate is configured with the right code, but the vat rate does not match, that rate is highlighted in red
 *
 * @param efstaRates The vat rates expected on the account
 */
export function TaxRateValidationTable({
  efstaRates,
}: {
  efstaRates: ExpectedTaxRate[];
}) {
  const vatRates = useSelector(getVatRates);

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell colSpan={4}>Expected vat rates</TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={3}>EFR expected vat rate</TableCell>
          <TableCell colSpan={2}>Erply vat rate</TableCell>
        </TableRow>
        <TableRow>
          <TableCell>class</TableCell>
          <TableCell>description</TableCell>
          <TableCell>rate</TableCell>
          <TableCell>rate</TableCell>
          <TableCell>name</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {efstaRates.map(({ rate, name, group }) => {
          const matches = vatRates.filter(rate => rate.code === group);
          const firstRateIsIncorrect =
            Number(matches[0]?.rate ?? rate) !== rate;
          return (
            <>
              <TableRow>
                <TableCell>{group}</TableCell>
                <TableCell>{name}</TableCell>
                <TableCell>{rate}</TableCell>
                <TableCell>
                  <Typography
                    color={firstRateIsIncorrect ? 'error' : undefined}
                  >
                    {matches[0]?.rate ?? ''}
                  </Typography>
                </TableCell>
                <TableCell>{matches[0]?.name ?? ''}</TableCell>
              </TableRow>
              {matches.slice(1).map(m => (
                <TableRow key={m.id}>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell>
                    <Typography
                      color={Number(m.rate) === rate ? undefined : 'error'}
                    >
                      {m.rate}
                    </Typography>
                  </TableCell>
                  <TableCell>{m.name}</TableCell>
                </TableRow>
              ))}
            </>
          );
        })}
        {vatRates
          .filter(r => !efstaRates.map(r => r.group).includes(r.code))
          .map((rate, i) => (
            <TableRow>
              <TableCell>{i === 0 && '-'}</TableCell>
              <TableCell>
                <Typography color="error">
                  {i === 0 && 'Not corresponding to any EFSTA code'}
                </Typography>
              </TableCell>
              <TableCell />
              <TableCell>
                <Typography color="error">{rate.rate}</Typography>
              </TableCell>
              <TableCell>{rate.name}</TableCell>
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
}
